import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const ResourcePage = () => {
  const { user, token, loading } = useAuth();
  const navigate = useNavigate();

  // Debug logging
  useEffect(() => {
    console.log('🔍 ResourcePage Auth State:', {
      user: user,
      token: token,
      loading: loading,
      localStorageToken: localStorage.getItem('token')
    });
  }, [user, token, loading]);

  const handleJournalDownload = async (e) => {
    e.preventDefault();
    
    // Get token from localStorage as fallback
    const authToken = token || localStorage.getItem('token');
    
    console.log('📥 Download attempt:', {
      user: user,
      authToken: authToken,
      hasUser: !!user,
      hasToken: !!authToken
    });
    
    // Check if we have a token
    if (!authToken) {
      alert('Please log in to download the GNology Research Journal');
      navigate('/login');
      return;
    }

    try {
      const response = await fetch('/api/journal/pdf/download', {
        headers: {
          'Authorization': `Bearer ${authToken}`  // Fixed: Use Bearer token format
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          alert('Session expired. Please log in again.');
          // Clear invalid token
          localStorage.removeItem('token');
          navigate('/login');
          return;
        }
        throw new Error('Failed to download journal');
      }

      // Get the blob from the response
      const blob = await response.blob();
      
      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = 'GNology-Research-Journal.pdf';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      console.log('✅ Download successful!');
    } catch (error) {
      console.error('❌ Download error:', error);
      alert('Error downloading journal. Please try again.');
    }
  };

  // Show loading state
  if (loading) {
    return <div style={{padding: '2rem', textAlign: 'center'}}>Loading...</div>;
  }

  return (
    <div style={{maxWidth: '1200px', margin: '0 auto', padding: 'clamp(1rem, 3vw, 2rem)'}}>
      <h1>Gene Research Resources</h1>
      <p style={{color: '#666', marginBottom: '2rem'}}>
        Helpful tools and guides to enhance your genealogical research experience with Gene.
      </p>
      
      {/* Tutorial Section */}
      <div style={{
        background: '#e8f5e9',
        border: '2px solid #2e7d32',
        borderRadius: '12px',
        padding: '2rem',
        marginBottom: '2rem',
        textAlign: 'center'
      }}>
        <h2 style={{color: '#2e7d32', marginBottom: '1rem'}}>
          📚 New to Gene? Start Here!
        </h2>
        <p style={{fontSize: '1.1rem', color: '#555', marginBottom: '1.5rem'}}>
          Our comprehensive tutorial walks you through everything you need to know about using Gene effectively.
        </p>
        <Link to="/tutorial" style={{
          display: 'inline-block',
          background: '#2e7d32',
          color: 'white',
          padding: '0.75rem 2rem',
          borderRadius: '50px',
          textDecoration: 'none',
          fontWeight: '600',
          fontSize: '1.1rem',
          transition: 'transform 0.3s'
        }} onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'} 
           onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}>
          View Complete Tutorial →
        </Link>
      </div>

      {/* GNology Journal PDF Section - UPDATED WITH AUTH */}
      <div style={{
        background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
        color: 'white',
        padding: '2rem',
        borderRadius: '12px',
        marginBottom: '3rem',
        boxShadow: '0 4px 12px rgba(21, 101, 192, 0.3)'
      }}>
        <div style={{textAlign: 'center', marginBottom: '2rem'}}>
          <h2 style={{fontSize: '2rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px'}}>
            📄 GNology Research Journal
          </h2>
          <p style={{fontSize: '1.2rem', opacity: '0.9', maxWidth: '600px', margin: '0 auto'}}>
            Downloadable PDF journal template for organizing your genealogical research using the Generational Nexus methodology.
          </p>
          {!user && !localStorage.getItem('token') && (
            <p style={{
              fontSize: '1rem',
              marginTop: '1rem',
              background: 'rgba(255, 255, 255, 0.2)',
              padding: '0.5rem 1rem',
              borderRadius: '20px',
              display: 'inline-block'
            }}>
              🔒 Login required to download
            </p>
          )}
        </div>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '1.5rem',
          marginBottom: '2rem'
        }}>
          <div style={{background: 'rgba(255, 255, 255, 0.1)', padding: '1.5rem', borderRadius: '8px'}}>
            <h3 style={{marginBottom: '1rem'}}>🎯 What's Included</h3>
            <ul style={{listStyle: 'none', padding: 0, lineHeight: '1.6'}}>
              <li>✅ 5-Ancestor Research Framework</li>
              <li>✅ Goal Setting & Progress Tracking</li>
              <li>✅ Source Citation Templates</li>
              <li>✅ Timeline Creation Worksheets</li>
              <li>✅ Research Planning Pages</li>
            </ul>
          </div>
          
          <div style={{background: 'rgba(255, 255, 255, 0.1)', padding: '1.5rem', borderRadius: '8px'}}>
            <h3 style={{marginBottom: '1rem'}}>🧬 GNology Method</h3>
            <ul style={{listStyle: 'none', padding: 0, lineHeight: '1.6'}}>
              <li>📊 Systematic Research Approach</li>
              <li>🔍 Evidence Analysis Framework</li>
              <li>📝 Documentation Standards</li>
              <li>🌳 Family Connection Mapping</li>
              <li>💡 Breakthrough Strategies</li>
            </ul>
          </div>
        </div>
        
        <div style={{textAlign: 'center'}}>
          <button 
            onClick={handleJournalDownload}
            style={{
              display: 'inline-block',
              background: 'white',
              color: '#1565c0',
              padding: '1rem 2.5rem',
              borderRadius: '50px',
              border: 'none',
              cursor: 'pointer',
              fontWeight: '700',
              fontSize: '1.2rem',
              transition: 'all 0.3s',
              boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'scale(1.05)';
              e.target.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'scale(1)';
              e.target.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.2)';
            }}
          >
            📥 Download GNology Journal PDF
          </button>
        </div>
        
        <div style={{textAlign: 'center', marginTop: '1.5rem', opacity: '0.8'}}>
          <p>🖨️ Print-friendly format • 📱 Mobile-compatible • 🔄 Reusable template</p>
        </div>
      </div>
      
      {/* Featured FamilySearch Section */}
      <div style={{
        background: 'linear-gradient(135deg, #2e7d32 0%, #4caf50 100%)',
        color: 'white',
        padding: 'clamp(1rem, 3vw, 2rem)',
        borderRadius: '12px',
        marginBottom: '3rem',
        boxShadow: '0 4px 12px rgba(46, 125, 50, 0.3)'
      }}>
        <div style={{textAlign: 'center', marginBottom: '2rem'}}>
          <h2 style={{fontSize: 'clamp(1.5rem, 4vw, 2rem)', marginBottom: '1rem'}}>
            🌟 Featured Resource: FamilySearch
          </h2>
          <p style={{fontSize: 'clamp(1rem, 2.5vw, 1.2rem)', opacity: '0.9'}}>
            The world's largest free genealogy resource with billions of historical records
          </p>
        </div>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: 'clamp(1rem, 3vw, 2rem)',
          marginBottom: '2rem'
        }}>
          <div>
            <h3 style={{fontSize: 'clamp(1.1rem, 3vw, 1.3rem)', marginBottom: '1rem'}}>
              📊 What You'll Find
            </h3>
            <ul style={{listStyle: 'none', padding: 0, lineHeight: '1.6'}}>
              <li>🔍 Census records from multiple countries</li>
              <li>📜 Birth, marriage, and death certificates</li>
              <li>⚰️ Cemetery and burial records</li>
              <li>🚢 Immigration and passenger lists</li>
              <li>🏛️ Historical newspapers and city directories</li>
            </ul>
          </div>
          
          <div>
            <h3 style={{fontSize: 'clamp(1.1rem, 3vw, 1.3rem)', marginBottom: '1rem'}}>
              💡 Pro Tips for Success
            </h3>
            <ul style={{listStyle: 'none', padding: 0, lineHeight: '1.6'}}>
              <li>🎯 Start with what you know for certain</li>
              <li>📅 Search by approximate dates, not exact ones</li>
              <li>🔤 Try different spelling variations</li>
              <li>👥 Look for siblings and relatives in the same records</li>
              <li>💾 Always save and cite your sources</li>
            </ul>
          </div>
        </div>
        
        <div style={{textAlign: 'center'}}>
          <a 
            href="https://www.familysearch.org" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{
              display: 'inline-block',
              background: 'white',
              color: '#2e7d32',
              padding: 'clamp(0.5rem, 2vw, 0.75rem) clamp(1.5rem, 4vw, 2rem)',
              borderRadius: '50px',
              textDecoration: 'none',
              fontWeight: '600',
              fontSize: 'clamp(1rem, 2.5vw, 1.1rem)',
              transition: 'transform 0.3s'
            }}
            onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
            onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
          >
            Visit FamilySearch →
          </a>
        </div>
      </div>

      {/* Quick Reference Guide */}
      <div style={{marginBottom: '3rem'}}>
        <h2 style={{textAlign: 'center', marginBottom: '2rem', color: '#333'}}>
          📋 Quick Reference Guide
        </h2>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '2rem'
        }}>
          {/* Research Checklist */}
          <div style={{
            background: '#f8f9fa',
            border: '1px solid #e9ecef',
            borderRadius: '8px',
            padding: '1.5rem'
          }}>
            <h3 style={{color: '#2e7d32', marginBottom: '1rem'}}>
              ✅ Research Checklist
            </h3>
            <ul style={{lineHeight: '1.8', color: '#555'}}>
              <li>🎯 Define specific research goals</li>
              <li>📝 Document sources and citations</li>
              <li>🔍 Verify information with multiple sources</li>
              <li>👨‍👩‍👧‍👦 Research entire families, not just direct lines</li>
              <li>📅 Create timelines for life events</li>
              <li>🗂️ Organize findings systematically</li>
            </ul>
          </div>
          
          {/* Common Record Types */}
          <div style={{
            background: '#f8f9fa',
            border: '1px solid #e9ecef',
            borderRadius: '8px',
            padding: '1.5rem'
          }}>
            <h3 style={{color: '#2e7d32', marginBottom: '1rem'}}>
              📄 Essential Record Types
            </h3>
            <ul style={{lineHeight: '1.8', color: '#555'}}>
              <li>📊 <strong>Census Records:</strong> Household information</li>
              <li>📜 <strong>Vital Records:</strong> Birth, marriage, death</li>
              <li>⚰️ <strong>Cemetery Records:</strong> Burial information</li>
              <li>🏛️ <strong>Probate Records:</strong> Wills and estates</li>
              <li>📰 <strong>Newspapers:</strong> Obituaries and announcements</li>
              <li>🚢 <strong>Immigration:</strong> Passenger lists and naturalization</li>
            </ul>
          </div>
          
          {/* Research Tips */}
          <div style={{
            background: '#f8f9fa',
            border: '1px solid #e9ecef',
            borderRadius: '8px',
            padding: '1.5rem'
          }}>
            <h3 style={{color: '#2e7d32', marginBottom: '1rem'}}>
              💡 Research Tips
            </h3>
            <ul style={{lineHeight: '1.8', color: '#555'}}>
              <li>🔤 Try alternate name spellings</li>
              <li>📅 Search ±5 years from estimated dates</li>
              <li>🏘️ Research the whole neighborhood</li>
              <li>👥 Follow siblings and associates</li>
              <li>📚 Use local historical societies</li>
              <li>🧬 Consider DNA testing for connections</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Additional Resources */}
      <div>
        <h2 style={{textAlign: 'center', marginBottom: '2rem', color: '#333'}}>
          🔗 Additional Resources
        </h2>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '1.5rem'
        }}>
          <div style={{
            background: 'white',
            border: '1px solid #ddd',
            borderRadius: '8px',
            padding: '1.5rem',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{color: '#1976d2', marginBottom: '1rem'}}>
              🏛️ National Archives
            </h3>
            <p style={{color: '#666', marginBottom: '1rem'}}>
              Official US government records including military, immigration, and federal census data.
            </p>
            <a 
              href="https://www.archives.gov/research/genealogy"
              target="_blank"
              rel="noopener noreferrer"
              style={{color: '#1976d2', textDecoration: 'none', fontWeight: '500'}}
            >
              Visit Archives.gov →
            </a>
          </div>
          
          <div style={{
            background: 'white',
            border: '1px solid #ddd',
            borderRadius: '8px',
            padding: '1.5rem',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{color: '#1976d2', marginBottom: '1rem'}}>
              🧬 MyHeritage
            </h3>
            <p style={{color: '#666', marginBottom: '1rem'}}>
              Global genealogy platform with extensive international records and DNA matching.
            </p>
            <a 
              href="https://www.myheritage.com"
              target="_blank"
              rel="noopener noreferrer"
              style={{color: '#1976d2', textDecoration: 'none', fontWeight: '500'}}
            >
              Visit MyHeritage →
            </a>
          </div>
          
          <div style={{
            background: 'white',
            border: '1px solid #ddd',
            borderRadius: '8px',
            padding: '1.5rem',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{color: '#1976d2', marginBottom: '1rem'}}>
              📚 FindMyPast
            </h3>
            <p style={{color: '#666', marginBottom: '1rem'}}>
              Specializes in British and Irish records with unique historical newspaper collections.
            </p>
            <a 
              href="https://www.findmypast.com"
              target="_blank"
              rel="noopener noreferrer"
              style={{color: '#1976d2', textDecoration: 'none', fontWeight: '500'}}
            >
              Visit FindMyPast →
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResourcePage;
